import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import financialSubmissionService, { SubmittedFinancialData } from '../services/financialSubmissionService';
import organisationKPIsService, { OrganisationKPIs } from '../services/organisationKPIsService';
import organisationSettingsService, { OrganisationSettings } from '../services/organisationSettingsService';
import KPIDetailsModal from '../components/analytics/KPIDetailsModal';
import QualityCheckResultModal from '../components/analytics/QualityCheckResultModal';
import FinancialDataChatbot from '../components/chat/FinancialDataChatbot';
import SubmissionFilterPanel, { SubmissionFilter } from '../components/data/SubmissionFilterPanel';
import './SubmissionQualityCheckPage.css';

// Claude 4.5 Sonnet ARN model ID (primary model)
const CLAUDE_45_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1:586794487615:inference-profile/eu.anthropic.claude-sonnet-4-5-20250929-v1:0';

// Claude 3.7 Sonnet as fallback when 4.0 gets throttled
const CLAUDE_37_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1::inference-profile/eu.anthropic.claude-3-7-sonnet-20250219-v1:0';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const SubmissionQualityCheckPage: React.FC = () => {
  const { user } = useAuthStore();
  const [submissions, setSubmissions] = useState<SubmittedFinancialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessibleLocations, setAccessibleLocations] = useState<any[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [organisationSettings, setOrganisationSettings] = useState<OrganisationSettings | null>(null);
  
  // Modal state
  const [selectedSubmission, setSelectedSubmission] = useState<SubmittedFinancialData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orgKPIRules, setOrgKPIRules] = useState<OrganisationKPIs[]>([]);
  
  // Quality check result modal state
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [qualityCheckResult, setQualityCheckResult] = useState<{
    score: number;
    report: string;
    submissionId: number;
  } | null>(null);

  // AI Chatbot state
  const [showChatbot, setShowChatbot] = useState(false);

  // Filter state
  const [submissionFilter, setSubmissionFilter] = useState<SubmissionFilter>({
    locations: [],
    years: [],
    months: [],
    kpiCategories: [],
    includeAllQualityScores: false,
    intelligentFiltering: false
  });

  // Job status tracking
  const [jobStatuses, setJobStatuses] = useState<{[submissionId: number]: string}>({});
  
  // Refs to prevent duplicate API calls
  const locationsLoadingRef = useRef(false);
  const submissionsLoadingRef = useRef(false);

  // Memoized function to fetch accessible locations
  const fetchAccessibleLocations = useCallback(async () => {
    if (!user || locationsLoadingRef.current) return;
    
    console.log('🔄 Fetching accessible locations for user:', user.id, user.email);
    locationsLoadingRef.current = true;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📍 Locations API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📍 Locations API response data:', data);
        if (data.success) {
          console.log('✅ Setting accessible locations:', data.data.length, data.data);
          setAccessibleLocations(data.data);
        } else {
          console.log('❌ Locations API returned success: false');
          setAccessibleLocations([]);
        }
      } else {
        console.log('❌ Locations API returned non-OK status:', response.status);
        setAccessibleLocations([]);
      }
    } catch (error) {
      console.error('❌ Error fetching accessible locations:', error);
      setAccessibleLocations([]);
    } finally {
      setLocationsLoaded(true);
      locationsLoadingRef.current = false;
    }
  }, [user]);

  // Fetch accessible locations for the user
  useEffect(() => {
    fetchAccessibleLocations();
  }, [fetchAccessibleLocations]);

  // Memoized function to fetch organization KPI rules
  const fetchOrgKPIRules = useCallback(async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Fetching organization KPI rules...');
      const rules = await organisationKPIsService.getOrganisationKPIs();
      console.log('📋 Organization KPI rules received:', rules.length);
      setOrgKPIRules(rules);
    } catch (err) {
      console.error('❌ Error fetching organization KPI rules:', err);
      // Don't set error state for KPI rules as it's not critical for viewing submissions
    }
  }, [user]);

  // Memoized function to fetch organization settings
  const fetchOrganisationSettings = useCallback(async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Fetching organisation settings...');
      const settings = await organisationSettingsService.getOrganisationSettings();
      console.log('⚙️ Organisation settings received:', settings);
      setOrganisationSettings(settings);
    } catch (err) {
      console.error('❌ Error fetching organisation settings:', err);
      // Don't set error state for settings as it's not critical for viewing submissions
    }
  }, [user]);

  // Memoized function to fetch submissions
  const fetchSubmissions = useCallback(async () => {
    if (!user || !locationsLoaded || submissionsLoadingRef.current) return;
    
    console.log('🔄 Fetching financial submissions...');
    console.log('📍 Accessible locations:', accessibleLocations.length, accessibleLocations.map(loc => `${loc.id}: ${loc.name || loc.locationName}`));
    submissionsLoadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const data = await financialSubmissionService.getFinancialSubmissions();
      console.log('📊 Total submissions fetched:', data.length);
      
      // Filter submissions based on accessible locations and quality score logic
      const filteredSubmissions = data.filter(submission => {
        // Include submissions without quality scores (original logic)
        const hasNoQualityScore = submission.dataQualityScore === null || submission.dataQualityScore === undefined;
        
        // Include submissions with quality scores below validation lower threshold
        const hasBelowThresholdScore = organisationSettings && 
          submission.dataQualityScore !== null && 
          submission.dataQualityScore !== undefined && 
          submission.dataQualityScore < organisationSettings.validationLower;
        
        // Exclude submissions that have quality scores at or above the validation lower threshold
        if (!hasNoQualityScore && !hasBelowThresholdScore) {
          return false;
        }
        
        // If submission has no location ID, include it (for backwards compatibility)
        if (!submission.locationId) {
          return true;
        }
        
        // If user has no accessible locations, exclude all location-specific submissions
        if (accessibleLocations.length === 0) {
          console.log('❌ No accessible locations - excluding submission:', submission.id);
          return false;
        }
        
        // Check if user has access to this location
        const hasAccess = accessibleLocations.some(loc => loc.id === submission.locationId);
        if (!hasAccess) {
          console.log('❌ No access to location', submission.locationId, 'for submission:', submission.id);
        }
        return hasAccess;
      });
      
      console.log('✅ Filtered submissions for quality check:', filteredSubmissions.length);
      setSubmissions(filteredSubmissions);
    } catch (err: any) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submitted financial data');
    } finally {
      setLoading(false);
      submissionsLoadingRef.current = false;
    }
  }, [user, locationsLoaded, accessibleLocations, organisationSettings]);

  // Fetch organization KPI rules
  useEffect(() => {
    fetchOrgKPIRules();
  }, [fetchOrgKPIRules]);

  // Fetch organisation settings
  useEffect(() => {
    fetchOrganisationSettings();
  }, [fetchOrganisationSettings]);

  // Manual refresh function for job statuses
  const refreshJobStatuses = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/quality-check/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        const jobs = result.data || [];

        // Update job statuses (merge with existing statuses)
        setJobStatuses(prev => {
          const updated = { ...prev };
          jobs.forEach((job: any) => {
            if (job.submissionId) {
              console.log(`📋 Job status update: Submission ${job.submissionId} -> ${job.status}`);
              updated[job.submissionId] = job.status;
            }
          });
          return updated;
        });

        // Check if any jobs have completed
        const completedJobs = jobs.filter((job: any) => 
          job.status === 'completed' && job.result
        );

        if (completedJobs.length > 0) {
          console.log('📋 Found completed quality checks, refreshing submissions');
          await fetchSubmissions();
          
          // Clear completed job statuses after a delay so user can see the completion
          setTimeout(() => {
            setJobStatuses(prev => {
              const updated = { ...prev };
              completedJobs.forEach((job: any) => {
                if (job.submissionId) {
                  delete updated[job.submissionId];
                }
              });
              return updated;
            });
          }, 3000); // Clear after 3 seconds
        }
      }
    } catch (error) {
      console.error('Error refreshing job statuses:', error);
    }
  }, [fetchSubmissions]);

  // Fetch submitted financial data
  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Check job statuses on page load
  useEffect(() => {
    if (user) {
      refreshJobStatuses();
    }
  }, [user, refreshJobStatuses]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getLocationName = useCallback((locationId?: number) => {
    if (!locationId) return 'Organization Level';
    const location = accessibleLocations.find(loc => loc.id === locationId);
    return location ? location.name : `Location ${locationId}`;
  }, [accessibleLocations]);

  const getMonthName = useCallback((month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || month.toString();
  }, []);

  // Apply filters to submissions
  const filteredSubmissions = useMemo(() => {
    let filtered = submissions;

    // Filter by locations
    if (submissionFilter.locations.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.locations.includes(getLocationName(submission.locationId))
      );
    }

    // Filter by years
    if (submissionFilter.years.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.years.includes(submission.year)
      );
    }

    // Filter by months
    if (submissionFilter.months.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.months.includes(submission.month)
      );
    }

    return filtered;
  }, [submissions, submissionFilter, getLocationName]);

  // Transform filtered submissions into format for AI chatbot, excluding quality reports
  const aiDataFilter = useMemo(() => {
    const filter = {
      // Add explicit flag to indicate this is submission data
      isSubmissionData: true,
      // Extract unique values from filtered submissions
      years: Array.from(new Set(filteredSubmissions.map(s => s.year))),
      months: Array.from(new Set(filteredSubmissions.map(s => s.month))),
      locations: Array.from(new Set(filteredSubmissions.map(s => getLocationName(s.locationId)).filter(Boolean))),
      // Include filtered submission data (without quality reports) with location names
      submissionData: filteredSubmissions.map(submission => {
        const { dataQualityReport, ...submissionWithoutReport } = submission;
        return {
          ...submissionWithoutReport,
          locationName: getLocationName(submission.locationId) // Add readable location name
        };
      }),
      limit: filteredSubmissions.length
    };
    
    console.log('🚀 Quality Check aiDataFilter created:', {
      isSubmissionData: filter.isSubmissionData,
      submissionDataCount: filter.submissionData.length,
      sampleSubmission: filter.submissionData[0],
      years: filter.years,
      locations: filter.locations
    });
    
    return filter;
  }, [filteredSubmissions, getLocationName]);

  // Modal handlers
  const handleViewSubmission = useCallback((submission: SubmittedFinancialData) => {
    setSelectedSubmission(submission);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSubmission(null);
  }, []);

  // Filter handlers
  const handleApplyFilter = useCallback((filter: SubmissionFilter) => {
    setSubmissionFilter(filter);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSubmissionFilter({
      locations: [],
      years: [],
      months: [],
      kpiCategories: [],
      includeAllQualityScores: false,
      intelligentFiltering: false
    });
  }, []);

  // Function to create CSV content from submission data
  const createCSVFromSubmission = (submission: SubmittedFinancialData): string => {
    console.log('📊 Creating CSV from submission data...');
    console.log('📈 Total KPIs in submission:', submission.kpiData.length);
    
    // Create headers for the CSV
    const headers = ['Location', 'Year', 'Month', 'KPI Category', 'KPI Name', 'KPI Description', 'KPI Value'];
    
    // Create rows from KPI data
    const rows = submission.kpiData.map(kpi => [
      getLocationName(submission.locationId), // Use actual location name instead of generic ID
      submission.year.toString(),
      submission.month.toString(),
      kpi.category || 'Unknown',
      kpi.name || 'Unknown',
      kpi.description || kpi.name || 'Unknown',
      kpi.value !== null ? kpi.value.toString() : 'N/A'
    ]);
    
    console.log('📋 Created CSV rows:', rows.length);
    
    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    console.log('📄 Final CSV length (characters):', csvContent.length);
    console.log('📄 Final CSV lines:', csvContent.split('\n').length);
    
    return csvContent;
  };

  // Function to create quality analysis prompt
  const createQualityPrompt = (submission: SubmittedFinancialData): string => {
    if (orgKPIRules.length === 0) {
      return `You are an expert financial data quality analyst. Please analyze the provided KPI data for data quality issues and provide a comprehensive quality score (0-100) and detailed report.

CRITICAL SCORING METHODOLOGY - FOLLOW EXACTLY:
1. **STARTING SCORE**: Begin with exactly 100 points
2. **RULE EVALUATION**: For each data quality issue found, determine severity and assign point deductions
3. **DEDUCTION SYSTEM**: 
   - Critical issues (missing data, invalid formats): -10 to -20 points each
   - Major issues (outliers, inconsistencies): -5 to -10 points each  
   - Minor issues (formatting, completeness): -1 to -5 points each
4. **FINAL SCORE**: Final score = 100 - (sum of all deductions)

**MANDATORY ARITHMETIC VERIFICATION:**
- ALWAYS double-check your basic arithmetic: addition, subtraction
- When calculating total deductions, ADD each deduction one by one
- When calculating final score, use EXACTLY: 100 - (total deductions)
- If you get different results, STOP and recalculate from scratch
- Show your arithmetic step-by-step: "10 + 5 + 3 = 18, so 100 - 18 = 82"

Focus on:
1. Data completeness and consistency
2. Value ranges and outliers
3. Missing or invalid data points
4. Data format and structure issues
5. Business logic validation

Provide your response in the following format:
QUALITY_SCORE: [0-100]

QUALITY_REPORT:
## CALCULATION DETAILS

### DATA QUALITY EVALUATION
**Starting Score: 100**

**Issue Analysis:**
[For each issue found, show:
- Issue Description: [What was found]
- Severity: [Critical/Major/Minor]  
- Point Deduction: [-X points]
- Data Value: [Show the actual problematic value]]

**Running Score Calculation:**
- Starting Score: 100
- Total Deductions: [Sum of all deductions]
- **Final Score: [100 - Total Deductions]**

### VERIFICATION CHECK
[Double-check arithmetic and re-verify each deduction]

## DETAILED ANALYSIS
[Comprehensive analysis of data quality issues, recommendations, and findings]`;
    }
    
    // Create rules description for the prompt
    const rulesDescription = orgKPIRules.map(rule => 
      `"${rule.ruleText}" (Rule ID: ${rule.ruleId}, Weight: ${rule.ruleWeight})`
    ).join('\n');
    
    const totalWeight = orgKPIRules.reduce((sum, rule) => sum + rule.ruleWeight, 0);
    
    return `You are an expert financial data quality analyst. Please analyze the provided KPI data against the following organizational validation rules and provide a comprehensive quality score (0-100) and detailed report.

ORGANIZATIONAL KPI VALIDATION RULES WITH WEIGHTS:
${rulesDescription}

CRITICAL SCORING METHODOLOGY - FOLLOW EXACTLY:
You MUST use this exact scoring system for consistent, repeatable results:

**IMPORTANT: SINGLE SCORE ONLY**
- Do NOT show preliminary/incorrect scores followed by corrections
- ONLY show the final, accurate score after proper verification
- Verify all rules internally before writing any scores

1. **STARTING SCORE**: Begin with exactly 100 points
2. **RULE EVALUATION**: For each validation rule, determine if the data PASSES or FAILS the rule
3. **WEIGHT DEDUCTION**: If data FAILS a rule, deduct the EXACT weight of that rule from the score
4. **PASS/FAIL ONLY**: There are no partial failures - each rule is either PASS (0 deduction) or FAIL (full weight deduction)
5. **ADDITIONAL QUALITY CHECKS**: Also evaluate general data quality and deduct points for other issues
6. **FINAL SCORE**: Final score = 100 - (sum of weights for all failed rules + additional deductions)

SCORING EXAMPLES:
- Data passes all rules and has no issues: Score = 100
- Data fails rule with weight 15: Score = 100 - 15 = 85
- Data fails rules with weights 10 and 8: Score = 100 - 10 - 8 = 82
- Data fails rule with weight 12 + has 5 points of other issues: Score = 100 - 12 - 5 = 83

**MANDATORY ARITHMETIC VERIFICATION:**
- ALWAYS double-check your basic arithmetic: addition, subtraction
- When calculating total deductions, ADD each failed rule weight one by one
- When calculating final score, use EXACTLY: 100 - (total deductions)
- If you get different results, STOP and recalculate from scratch
- Show your arithmetic step-by-step: "15 + 8 + 5 = 28, so 100 - 28 = 72"

**RULE EVALUATION PRECISION:**
- Read each rule condition EXACTLY as written
- Compare the actual data value to the rule threshold PRECISELY
- "below X" means the value must be < X (strictly less than)
- "above X" means the value must be > X (strictly greater than)
- "minimum X" means the value must be >= X (greater than or equal)
- If a value equals the threshold, determine PASS/FAIL based on the exact wording
- NEVER assume a rule fails - verify the exact condition

Provide your response in the following format:
QUALITY_SCORE: [0-100]

QUALITY_REPORT:
## CALCULATION DETAILS

### RULE EVALUATION
**Starting Score: 100**

**Rule Analysis:**
${orgKPIRules.map((rule, index) => `
${index + 1}. **Rule**: "${rule.ruleText}" (Weight: ${rule.ruleWeight})
   - **Data Value**: [Show the actual KPI value from the data]
   - **Evaluation**: [PASS/FAIL with clear reasoning]
   - **Score Impact**: [If FAIL: -${rule.ruleWeight} points, if PASS: No deduction]`).join('')}

**Additional Quality Issues:**
[List any other data quality issues found with point deductions]

**Running Score Calculation:**
- Starting Score: 100
- Rule Violation Deductions: [Sum of failed rule weights]
- Additional Issue Deductions: [Sum of other deductions]
- Total Deductions: [Sum of all deductions]
- **Final Score: [100 - Total Deductions]**

### VERIFICATION CHECK
[Double-check each rule calculation and arithmetic]

## DETAILED ANALYSIS
[Comprehensive analysis including rule compliance, data quality issues, recommendations, and findings]`;
  };

  // Function to run quality check
  const handleRunQualityCheck = async (submission: SubmittedFinancialData) => {
    if (!submission.id) {
      alert('Invalid submission ID');
      return;
    }

    // Prevent multiple clicks - check if already in progress
    const currentStatus = jobStatuses[submission.id];
    if (currentStatus && ['queuing', 'queued', 'processing'].includes(currentStatus)) {
      console.log('🚫 Quality check already in progress for submission:', submission.id);
      return;
    }

    try {
      console.log('🔍 Queuing quality check for submission:', submission.id);
      
      // Set status to queuing immediately for UI feedback
      setJobStatuses(prev => ({
        ...prev,
        [submission.id!]: 'queuing'
      }));
      
      const response = await fetch(`${API_BASE_URL}/quality-check/queue/${submission.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Reset status on error
        setJobStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[submission.id!];
          return newStatuses;
        });
        throw new Error(errorData.message || `Failed to queue quality check: ${response.statusText}`);
      }

      const result = await response.json();
      
      console.log('✅ Quality check queued successfully:', result.jobId);
      
      // Update status to queued
      setJobStatuses(prev => ({
        ...prev,
        [submission.id!]: 'queued'
      }));
      
      // Show success message
      alert('Quality check queued! You can continue using the system while it processes. Click "Refresh Status" to check progress.');
      
      // Refresh submissions to show the new status
      await fetchSubmissions();
      
    } catch (error: any) {
      console.error('❌ Error queuing quality check:', error);
      alert(`Failed to queue quality check: ${error.message}`);
    }
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <span className="breadcrumb-current">Submission Quality Check</span>
          </div>

          <div className="page-header">
            <div className="header-content">
              <div>
                <h1>Submission Quality Check</h1>
                <p className="page-description">
                  Review and analyze the quality of submitted financial data
                </p>
              </div>
              <button 
                className="refresh-button"
                onClick={refreshJobStatuses}
                title="Refresh job statuses"
              >
                🔄 Refresh Status
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          <SubmissionFilterPanel
            submissions={submissions}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilters}
            getLocationName={getLocationName}
          />

          <div className="content-container">
            {!user ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading user data...</p>
              </div>
            ) : loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading submitted financial data...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <div className="error-icon">⚠️</div>
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <button 
                  className="retry-button"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>No Pending Quality Checks</h3>
                <p>
                  {submissions.length === 0 
                    ? "All submitted financial data for your accessible locations has already been quality checked."
                    : `No submissions match your current filters. ${submissions.length} total submissions available.`
                  }
                </p>
              </div>
            ) : (
              <div className="submissions-table-container">
                <div className="table-header">
                  <h3>Pending Quality Checks ({filteredSubmissions.length}{filteredSubmissions.length !== submissions.length ? ` of ${submissions.length}` : ''})</h3>
                </div>
                
                <div className="submissions-table">
                  <div className="table-header-row">
                    <div className="table-cell header">Location</div>
                    <div className="table-cell header">Period</div>
                    <div className="table-cell header">KPIs</div>
                    <div className="table-cell header">Submitted</div>
                    <div className="table-cell header">Quality Score</div>
                    <div className="table-cell header">Actions</div>
                  </div>
                  
                  {filteredSubmissions.map((submission) => (
                    <div key={submission.id} className="table-row">
                      <div className="table-cell">
                        <div className="location-info">
                          <strong>{getLocationName(submission.locationId)}</strong>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="period-info">
                          <strong>{getMonthName(submission.month)} {submission.year}</strong>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="kpi-count">
                          <span className="count-badge">{submission.kpiData.length}</span>
                          <span className="count-label">KPIs</span>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="submitted-info">
                          <div className="submitted-date">
                            {submission.createdAt ? formatDate(submission.createdAt) : 'Unknown'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="quality-score">
                          {submission.dataQualityScore !== null && submission.dataQualityScore !== undefined ? (
                            <span className={`quality-badge ${submission.dataQualityScore >= 80 ? 'good' : submission.dataQualityScore >= 60 ? 'fair' : 'poor'}`}>
                              {submission.dataQualityScore}
                            </span>
                          ) : (
                            <span className="quality-badge pending">
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="action-buttons">
                          <button 
                            className="view-button"
                            onClick={() => handleViewSubmission(submission)}
                          >
                            View
                          </button>
                          {submission.dataQualityScore !== null && submission.dataQualityScore !== undefined && (
                            <button 
                              className="quality-report-button"
                              onClick={() => {
                                setQualityCheckResult({
                                  score: submission.dataQualityScore!,
                                  report: submission.dataQualityReport || 'No quality report available.',
                                  submissionId: submission.id!
                                });
                                setIsQualityModalOpen(true);
                              }}
                            >
                              View Report
                            </button>
                          )}
                          {(() => {
                            const jobStatus = submission.id ? jobStatuses[submission.id] : undefined;
                            const isInProgress = jobStatus && ['queuing', 'queued', 'processing'].includes(jobStatus);
                            
                            return (
                              <>
                                <button 
                                  className={`quality-check-button ${isInProgress ? 'in-progress' : ''}`}
                                  onClick={() => handleRunQualityCheck(submission)}
                                  disabled={!!isInProgress}
                                >
                                  {(() => {
                                    if (jobStatus === 'queuing') {
                                      return (
                                        <>
                                          <div className="spinner-small"></div>
                                          Queuing...
                                        </>
                                      );
                                    }
                                    if (jobStatus === 'queued') {
                                      return (
                                        <>
                                          <div className="spinner-small"></div>
                                          Queued
                                        </>
                                      );
                                    }
                                    if (jobStatus === 'processing') {
                                      return (
                                        <>
                                          <div className="spinner-small"></div>
                                          Processing...
                                        </>
                                      );
                                    }
                                    if (jobStatus === 'completed') {
                                      return (
                                        <>
                                          ✅ Completed
                                        </>
                                      );
                                    }
                                    if (jobStatus === 'failed') {
                                      return (
                                        <>
                                          ❌ Failed - Retry
                                        </>
                                      );
                                    }
                                    return 'Run Quality Check';
                                  })()}
                                </button>
                                {jobStatus && ['queued', 'processing'].includes(jobStatus) && (
                                  <button 
                                    className="refresh-status-button"
                                    onClick={refreshJobStatuses}
                                    title="Check job status"
                                  >
                                    🔄
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Details Modal */}
      {selectedSubmission && (
        <KPIDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          kpiData={selectedSubmission.kpiData}
          submissionInfo={{
            location: getLocationName(selectedSubmission.locationId),
            period: `${getMonthName(selectedSubmission.month)} ${selectedSubmission.year}`,
            submittedDate: formatDate(selectedSubmission.createdAt || '')
          }}
        />
      )}

      {/* Quality Check Result Modal */}
      {qualityCheckResult && (
        <QualityCheckResultModal
          isOpen={isQualityModalOpen}
          onClose={() => {
            setIsQualityModalOpen(false);
            setQualityCheckResult(null);
          }}
          qualityScore={qualityCheckResult.score}
          qualityReport={qualityCheckResult.report}
          submissionId={qualityCheckResult.submissionId}
        />
      )}

      {/* Chatbot Toggle Button - Outside content container for proper fixed positioning */}
      <div className="chatbot-toggle-container">
        <button 
          className="chatbot-toggle-button"
          onClick={() => setShowChatbot(!showChatbot)}
          title={showChatbot ? 'Close AI Assistant' : 'Open AI Assistant'}
        >
          <span className="chatbot-toggle-icon">💬</span>
          <span className="chatbot-toggle-text">
            {showChatbot ? 'Close AI Assistant' : 'Ask AI About Quality Checks'}
          </span>
          <span className="chatbot-toggle-count">
            {filteredSubmissions.length} submissions
          </span>
        </button>
      </div>

      {/* AI Data Assistant Chatbot */}
      <FinancialDataChatbot
        currentFilter={aiDataFilter}
        currentDataCount={filteredSubmissions.length}
        isVisible={showChatbot}
        onClose={() => setShowChatbot(false)}
      />
    </div>
  );
};

export default SubmissionQualityCheckPage;
